/**
 * QL-e — Verified ID: a read-only fact that an external authority verified a
 * subject's identity at a point in time.
 *
 * The subject is the client, or — for a Company/Group, or a Person acting
 * through a POA/guardian — their acting representatives (QL-654: one or several,
 * each verified for themselves), exactly as `getActingRepresentatives` resolves
 * them for screening and signing.
 *
 * Two sources, both recorded ONLY when a flow instance completes:
 *
 *   idura_signing  the signer authenticated with a national eID. Criipto binds
 *                  the signature to the client's national id via
 *                  `evidenceValidation`, so the identity is bound to the client
 *                  BY CONSTRUCTION — a live test confirmed a mismatched signer
 *                  is refused outright ("Signatory evidence validation failed").
 *   sumsub         a document + liveness check. Sumsub answers "is this a
 *                  genuine document held by the person on camera", NOT "is that
 *                  person this client" — a live sandbox run verified a
 *                  "FREYA KRAUSE" passport against a client named "Lars Larwe"
 *                  and was accepted. So this source is gated by
 *                  `identityMismatches` below; Idura needs no such gate.
 *
 * This module is pure and shared: the web renders the badge and modal from the
 * same types, and the comparison rules have exactly one implementation so the
 * public flow page, the internal flow page and the Sumsub webhook cannot drift.
 */

import { toAlpha2 } from "../dictionary/countries";

/** Where a verification came from. */
export type IdentityVerificationSource = "idura_signing" | "sumsub";

/**
 * The identity a verification is ABOUT, as our record held it at the moment of
 * verification. Deliberately our record and not the source's attestation —
 * Sumsub returns no national id at all, so hashing what the source attested
 * would leave a Sumsub badge standing when the SSN is edited.
 */
export type IdentitySubject = {
  kind: "self" | "representative";
  /**
   * The `client_party` row this subject came from, when a registered
   * representative. Null for the client themself and for a Group representative
   * resolved through `client_group_member` — neither is a party row, and
   * correcting their identity is not this feature's job.
   *
   * Carried so a correction lands on the RIGHT record: writing a
   * representative's passport details onto the company they act for is the
   * exact confusion QP-184 exists to prevent.
   */
  clientPartyId?: string | null;
  /**
   * QL-654 — the id of the person within a multi-subject verification: the
   * `client_party.id` of a registered representative, or the flagged group
   * member's id. Null for the client themself.
   */
  subjectId?: string | null;
  name: string | null;
  ssn: string | null;
  dob: string | null;
  /** ISO 3166-1 alpha-2, as stored on the client / party. */
  country: string | null;
};

/** One verification, as the modal shows it. */
export type IdentityVerification = {
  id: string;
  source: IdentityVerificationSource;
  verifiedAt: string;
  subjectKind: "self" | "representative";
  subjectName: string | null;
  isTestMode: boolean;
  /**
   * True when this verification's basis still matches the subject's CURRENT
   * identity. Only the newest verification drives the icon (see
   * `IdentityVerificationSummary`); older ones are history either way.
   */
  isCurrent: boolean;
  /**
   * Whether this is the person's standing (newest) verification: the newest
   * overall for a single person, the newest per person when several are verified.
   */
  isStanding: boolean;
  /** eID provider (`no:bankid`) for signing; document type for Sumsub. */
  method: string | null;
  /** ISO 3166-1 alpha-3 issuing country, Sumsub only. */
  documentCountry: string | null;
};

/**
 * What a list view needs: one boolean, resolved server-side.
 *
 * `verified` is the STANDING verification's validity — the most recent one.
 * A subject whose newest verification has gone stale shows no icon even if an
 * older one happens to match again after an edit was reverted: nobody
 * re-verified anything, and reviving a badge off a reverted string is an
 * artifact rather than evidence.
 */
export type IdentityVerificationSummary = {
  /**
   * With several people to verify (QL-654) this is true only when EVERY one of
   * them has a current verification; `subjects` says who has and who has not.
   */
  verified: boolean;
  /** QL-654 — one entry per person to verify, when there are 2+. Absent for a single person. */
  subjects?: IdentityVerificationSubjectStatus[];
  verifiedAt: string | null;
  source: IdentityVerificationSource | null;
  subjectKind: "self" | "representative" | null;
  subjectName: string | null;
  isTestMode: boolean;
};

/** QL-654 — one person's standing inside a multi-subject summary. */
export type IdentityVerificationSubjectStatus = {
  subjectId: string | null;
  name: string | null;
  verified: boolean;
};

// ── Comparison ───────────────────────────────────────────────────────────────

/**
 * `toAlpha2` — alpha-3 (`DEU`, what Sumsub reports) → alpha-2 (`DE`, what we
 * store). QL-68 moved the table to `../dictionary/countries`, where it covers every ISO
 * 3166-1 country instead of a hand-picked subset; the comparison rule is
 * unchanged — an unrecognised code is "cannot compare", never a mismatch (see
 * `identityMismatches`). Re-exported here so the types barrel keeps its shape.
 */
export { toAlpha2 };

/**
 * Normalise a name fragment for token comparison: lowercase, strip diacritics
 * and anything that is not a letter or a space.
 *
 * `Ø`/`Æ`/`Å` do not decompose under NFD, so they are mapped explicitly —
 * without that, "Bjørn" and "BJORN" would not match, which in Norway is not an
 * edge case.
 */
function normaliseNamePart(value: string): string {
  return value
    .toLowerCase()
    .replace(/ø/g, "o")
    .replace(/æ/g, "ae")
    .replace(/å/g, "a")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z\s]/g, " ")
    .trim();
}

/**
 * Does the document's name belong to the subject?
 *
 * Rule (settled with Helge): lowercase both sides, and require the document's
 * FIRST and LAST name each to appear as a whole token in the subject's name.
 *
 * Deliberately permissive in the directions that legitimately vary — middle
 * names, ordering, casing, diacritics — and strict in the one that matters,
 * that both parts are actually present. `FREYA` + `KRAUSE` matches
 * "Freya Anna Krause"; it does not match "Lars Larwe".
 *
 * A false mismatch blocks a real client, which is worse than the problem this
 * solves, so anything unknown returns TRUE (cannot contradict).
 */
export function nameMatchesSubject(
  subjectName: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): boolean {
  const subject = normaliseNamePart(subjectName ?? "");
  if (!subject) return true;

  const tokens = new Set(subject.split(/\s+/).filter(Boolean));
  const required = [firstName, lastName]
    .map((part) => normaliseNamePart(part ?? ""))
    .flatMap((part) => part.split(/\s+/).filter(Boolean));

  if (required.length === 0) return true;
  return required.every((token) => tokens.has(token));
}

/** What the document said, as far as this comparison cares. */
export type AttestedIdentity = {
  firstName?: string | null;
  lastName?: string | null;
  dob?: string | null;
  /** Alpha-2 or alpha-3; normalised internally. */
  country?: string | null;
};

export type IdentityMismatchField = "name" | "dob" | "country";

/**
 * Which fields CONTRADICT the subject. Empty means the document may stand as a
 * verification of this person.
 *
 * The rules, identical on the public page, the internal page and the webhook:
 *
 *   country  must match. A country we do not hold is a mismatch — "must match"
 *            cannot be satisfied by a value we lack, and `resolveScreeningPlan`
 *            already refuses a client without one, so this surfaces a client
 *            that is broken upstream rather than recording a verification that
 *            corroborates nothing.
 *   dob      must match WHEN WE HOLD ONE. A blank is not a contradiction; it is
 *            backfilled from the document at flow completion.
 *   name     token rule above.
 *
 * A country code we cannot interpret is NOT a mismatch. Blocking there would
 * punish a client for a gap in our own lookup table; the name and DOB rules
 * still apply, and the caller logs the code so the table can be extended.
 */
export function identityMismatches(
  subject: IdentitySubject,
  attested: AttestedIdentity,
): IdentityMismatchField[] {
  const mismatches: IdentityMismatchField[] = [];

  const ours = toAlpha2(subject.country);
  const theirs = toAlpha2(attested.country);
  if (!ours) {
    mismatches.push("country");
  } else if (theirs && theirs !== ours) {
    mismatches.push("country");
  }

  const subjectDob = subject.dob?.trim();
  const attestedDob = attested.dob?.trim();
  if (subjectDob && attestedDob && subjectDob !== attestedDob) {
    mismatches.push("dob");
  }

  if (!nameMatchesSubject(subject.name, attested.firstName, attested.lastName)) {
    mismatches.push("name");
  }

  return mismatches;
}

/**
 * The date of birth to backfill onto the subject, or null when there is nothing
 * to do.
 *
 * Fill only, never overwrite: a DIFFERING date is a mismatch and has already
 * blocked, so this can only ever fill a blank. That is why it may run
 * automatically while the advisor-facing "update client from verified document"
 * action, which rewrites a contradiction, requires a deliberate click.
 */
export function dobToBackfill(subject: IdentitySubject, attested: AttestedIdentity): string | null {
  if (subject.dob?.trim()) return null;
  return attested.dob?.trim() || null;
}

/**
 * What a Sumsub save reports back about whether the document actually
 * describes the subject.
 *
 * Returned by the save so BOTH the internal advisor page and the public client
 * page can react immediately — but it is only feedback. The authoritative gate
 * runs at flow completion, where the Verified ID is recorded, so a mismatch
 * cannot be talked past by a client that ignores the message.
 *
 * The verification result itself is still SAVED either way: the check happened,
 * and discarding it would lose the evidence that it did.
 */
export type SumsubIdentityCheck = {
  /** Empty when the document may stand as a verification of this subject. */
  mismatches: IdentityMismatchField[];
  /**
   * QL-f — the same conflicts with both values and their answered state, which
   * is what the resolution panel renders.
   *
   * Shown on the public page as well as the advisor's. That was decided
   * deliberately: a client who has just scanned their passport is the best
   * available evidence, and a conflict only ever appears once they have passed
   * a document and liveness check against a record that already disagrees — so
   * it cannot be used to fish for what we hold.
   */
  rows: IdentityConflictRow[];
  /** Conflicts still unanswered. Non-empty means the component blocks Submit. */
  unresolved: IdentityMismatchField[];
  /**
   * What the document said, for the ADVISOR-facing diff and the "update client
   * from verified document" action.
   *
   * Carried on both surfaces — see `rows`.
   */
  attested: AttestedIdentity;
};

/** GET /internal/clients/:id/identity — badge + modal in one payload. */
export type ClientIdentityResponse = {
  summary: IdentityVerificationSummary;
  verifications: IdentityVerification[];
};

// ── Conflict resolution ──────────────────────────────────────────────────────

/**
 * QL-f — one dismissed identity conflict.
 *
 * Only DISMISSALS are stored. Choosing "update client" resolves a conflict by
 * removing it: the record now agrees with the document, so `identityMismatches`
 * stops reporting it and there is nothing left to remember. Storing both
 * decisions would mean two ways to represent the same resolved state, and they
 * could disagree.
 *
 * Both values are captured because a dismissal is only valid for the pair it
 * was made about. Someone who dismisses "our 1985-03-21 vs the passport's
 * 1985-03-12" has judged THAT discrepancy; if the client's date is later edited
 * to something else that still disagrees, nobody has judged the new one, and it
 * must be asked again rather than inherit an answer given about different
 * facts.
 */
export type IdentityConflictResolution = {
  field: IdentityMismatchField;
  /** What our record said when this was dismissed. */
  ours: string | null;
  /** What the document said. */
  theirs: string | null;
  resolvedAt: string;
  /** Null when dismissed from a public flow session — nobody was signed in. */
  resolvedByUserId: string | null;
};

/** Our record's value for a mismatchable field, for comparison with a document. */
export function subjectValueFor(
  subject: IdentitySubject,
  field: IdentityMismatchField,
): string | null {
  if (field === "name") return subject.name?.trim() || null;
  if (field === "dob") return subject.dob?.trim() || null;
  return toAlpha2(subject.country);
}

/** The document's value for the same field, normalised the same way. */
export function attestedValueFor(
  attested: AttestedIdentity,
  field: IdentityMismatchField,
): string | null {
  if (field === "name") {
    const parts = [attested.firstName, attested.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  }
  if (field === "dob") return attested.dob?.trim() || null;
  return toAlpha2(attested.country);
}

/**
 * The mismatches nobody has answered yet.
 *
 * This is what gates Submit: a document that contradicts the record is not a
 * reason to block outright — it is a question — but it must be ANSWERED before
 * the flow can complete, by correcting the record or by saying explicitly that
 * the discrepancy is acceptable.
 *
 * A dismissal counts only while both sides still hold the values it was made
 * about, so editing either one re-opens the question.
 */
export function unresolvedMismatches(
  subject: IdentitySubject,
  attested: AttestedIdentity,
  resolutions: IdentityConflictResolution[],
): IdentityMismatchField[] {
  return identityMismatches(subject, attested).filter((field) => {
    const ours = subjectValueFor(subject, field);
    const theirs = attestedValueFor(attested, field);
    return !resolutions.some(
      (r) => r.field === field && (r.ours ?? null) === ours && (r.theirs ?? null) === theirs,
    );
  });
}

/**
 * The per-field rows the conflict panel renders: ours, theirs, and whether it
 * has been answered.
 *
 * Built here rather than in the component so the advisor page, the public page
 * and the completion gate all describe the same conflict the same way.
 */
export type IdentityConflictRow = {
  field: IdentityMismatchField;
  ours: string | null;
  theirs: string | null;
  resolved: boolean;
};

export function identityConflictRows(
  subject: IdentitySubject,
  attested: AttestedIdentity,
  resolutions: IdentityConflictResolution[],
): IdentityConflictRow[] {
  const unresolved = new Set(unresolvedMismatches(subject, attested, resolutions));
  return identityMismatches(subject, attested).map((field) => ({
    field,
    ours: subjectValueFor(subject, field),
    theirs: attestedValueFor(attested, field),
    resolved: !unresolved.has(field),
  }));
}

/** POST …/sumsub-verification/identity-conflict — answer one conflicting field. */
export type ResolveIdentityConflictRequest = {
  /**
   * QL-654 — whose conflict, when several people are verified (a
   * representative's `client_party.id`). Fixed to the person on a
   * representative's own session.
   */
  subjectId?: string;
  field: IdentityMismatchField;
  /** "update" corrects our record from the document; "dismiss" accepts it as-is. */
  decision: "update" | "dismiss";
};

export type ResolveIdentityConflictResponse = {
  /** Conflicts still unanswered. Empty means the component no longer blocks. */
  unresolved: IdentityMismatchField[];
  rows: IdentityConflictRow[];
};

// ── Change log ───────────────────────────────────────────────────────────────

/** The fields the identity change log covers. */
export type IdentityChangeField = "name" | "ssn" | "country" | "dateOfBirth" | "orgId";

/** Where an identity change came from. */
export type IdentityChangeSourceName =
  | "client_editor"
  | "trapets_lookup"
  | "sumsub_internal"
  | "sumsub_public"
  | "eid_login"
  | "flow_completion"
  | "public_api";

/**
 * One recorded change to a client's or representative's identity.
 *
 * `changedByName` is null when nobody was signed in — a public flow session or
 * an external public-API consumer. That is a meaningful answer rather than
 * missing data, and is read together with `source`.
 */
export type IdentityChangeEntry = {
  id: string;
  clientId: string;
  clientName: string | null;
  subjectKind: "self" | "representative";
  subjectName: string | null;
  field: IdentityChangeField;
  oldValue: string | null;
  newValue: string | null;
  source: IdentityChangeSourceName;
  changedAt: string;
  changedByName: string | null;
};

/** GET /internal/compliance/identity-changes, and the per-client variant. */
export type IdentityChangesResponse = {
  changes: IdentityChangeEntry[];
};
